<template>
  <div id="return-method" class="grid-x large-padding-top-m padding-top-s padding-bottom-s large-padding-bottom-xxxl border-top">
    <div class="h3 small-7 medium-3 medium-padding-right-xxxl" tabindex="0">Return Method</div>
    <div
      v-if="returnItemsStore.returnItemsCurrentStep >= (!returnItemsStore.SHOW_SHIPPING_ADDRESS_SECTION ? 3 : 4)"
      class="small-5 medium-2 align-top padding-top-xxs text-right medium-text-left"
    >
      <button
        v-if="!returnItemsStore.returnItemsEditable.returnMethod && returnItemsStore.returnSelections.returnMethod !== ''"
        class="link-body"
        @click="onChange"
      >
        Change
      </button>
    </div>
    <div
      v-if="returnItemsStore.returnItemsCurrentStep >= (!returnItemsStore.SHOW_SHIPPING_ADDRESS_SECTION ? 3 : 4)"
      class="small-12 medium-7 small-margin-top-s medium-margin-top-z large-padding-left-m"
    >
      <Notification v-if="showStarRewardsMessage" class="padding-xxs margin-bottom-s" severity="success" :closable="true">
        Congrats! You are now a Star Rewards member and can ship your returns for free.
      </Notification>
      <Notification v-if="returnItemsStore.isReturnMethodIneligible" class="margin-bottom-s" severity="error" :closable="false">
        The return method you selected is no longer available, please select another option.
      </Notification>
      <Notification v-if="showAreaRugErrorMessage" class="margin-bottom-s" severity="error" aria-live="assertive" :closable="false">
        This return contains an area rug. Area rugs cannot be returned in store. Please select another return method.
      </Notification>
      <div
        v-if="brand === 'mcom' && returnItemsStore.IS_ELIGIBLE_FOR_CONDITIONAL_RETURN_FEE && !returnItemsStore.hasEnrolledForStarRewards"
        class="join-star-rewards h5 grid-x padding-xs margin-bottom-xs"
        role="alert"
        :aria-label="`Ship your returns for free when you ${!UserStatus.isSignedIn() ? 'create an account & ' : ''}join Star Rewards`"
      >
        <div class="small-8 padding-right-xxs">
          Ship your returns for FREE when you
          <span v-if="!UserStatus.isSignedIn()">create an account & </span>join Star Rewards
        </div>
        <AppButton id="join-now" class="secondary button small-4" :loading="loadingJoinRewards" @click="joinNowHandler"> Join Now </AppButton>
      </div>

      <div v-if="returnItemsStore.returnItemsEditable.returnMethod">
        <CardRadio
          v-slot="{ option }"
          v-model="returnMethodChoice"
          value-key="id"
          :options="returnMethods"
          content-class="grid-x align-middle"
          @update:model-value="onCardClick"
        >
          <div class="cell auto" :aria-labelledby="'option-title-' + option.id + ' ' + 'instructions-' + option.id">
            <p :id="'option-title-' + option.id" class="large margin-bottom-xxs" v-text="option.title"></p>
            <ul :id="'instructions-' + option.id" class="small margin-bottom-xxs">
              <li v-for="instruction in option.instructions" :key="instruction" class="gray-1-color" v-text="instruction"></li>
            </ul>
            <div>
              <button
                :id="'locator-link-' + option.id"
                class="link-body"
                @keypress.enter.prevent="showLocatorSlideout($event, option.id)"
                @click="showLocatorSlideout($event, option.id)"
                v-text="locatorLinkText(option.locatorLinkText, option.id)"
              ></button>
            </div>
          </div>
          <div
            class="h5 small-3 text-right"
            v-text="
              returnItemsStore.IS_ELIGIBLE_FOR_CONDITIONAL_RETURN_FEE && !returnItemsStore.hasEnrolledForStarRewards
                ? getShipmentFee(option.id)
                : option.shipment
            "
          ></div>
        </CardRadio>

        <AppButton
          id="review-return"
          class="primary expanded"
          :disabled="!returnMethodChoice || isEstimatedRefundLoading"
          :loading="isEstimatedRefundLoading"
          @click="onContinue"
        >
          Review Return
        </AppButton>
      </div>
      <div v-else-if="selectedReturnMethodDetails" class="section-border">
        <p class="medium" v-text="selectedReturnMethodDetails?.title"></p>
        <ul>
          <li v-for="instruction in selectedReturnMethodDetails.instructions" :key="instruction" class="gray-1-color" v-text="instruction"></li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, markRaw, defineAsyncComponent } from 'vue';
import Notification from '@nuxt-common/atomic-ui/app/components/notification/Notification.vue';
import { UserStatus, Cookie } from '@nuxt-common/util/app/utils/cookie';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { getAnalyticsTitle, getTitle } from '@/utils/returns/ReturnInstruction';
import { createPayloadFromUopResponse } from '@/utils/returns/EstimatedRefund';
import AppButton from '@/components/AppButton';
import CardRadio from '@/components/CardRadio';
import { RETURN_METHODS, RETURN_METHOD_KEY_MAP, brand, REFUND_METHOD } from 'config';

import {
  CONDITIONAL_RETURN_FEE_ELIGIBLE_RETURN_METHODS,
  FREE_SHIPMENT,
  JOIN_STAR_REWARD_SLIDEOUT_TOGGLE_ID,
  LOCATOR_LOCATION_RADIUS,
  LOCATOR_RESULT_LIMIT,
} from '#shared/constants';

// Pinia Store & Composables
import { useReturnItemStore } from '@/stores/returns/useReturnItemStore';
import { useEstimatedRefundStore } from '@/stores/returns/useEstimatedRefundStore';
import { useJoinStarRewardsStore } from '@/stores/useJoinStarRewardsStore';

const returnMethodChoice = ref('');
const returnMethods = ref([]);
const isEstimatedRefundLoading = ref(false);
const showShippingFeeModal = ref(true);
const showStarRewardsMessage = ref(false);
const hasInteracted = ref(false);
const showAreaRugErrorMessage = ref(false);
const loadingJoinRewards = ref(false);

const estimatedRefundStore = useEstimatedRefundStore();
const joinStarRewardsStore = useJoinStarRewardsStore();
const returnItemsStore = useReturnItemStore();
const envPropsStore = useEnvPropsStore();
const route = useRoute();
const { registerModal, toggleModal } = useModal();

const { toggleSlideout, registerSlideout } = useSlideout();

const returnMethodsConfig = computed(() =>
  RETURN_METHODS.config(returnItemsStore.returnItems?.isBorisEnabled && returnItemsStore.IS_MARKETPLACE_ORDER),
);
const hasRebagItem = computed(() => returnItemsStore.returnSelections.returnItems.some((item) => item.returnChannel === 'DIGITAL'));
const hasEnrolledForStarRewards = computed(() => returnItemsStore.hasEnrolledForStarRewards);

const isReplaceOrExchangeRefundSelected = computed(() =>
  [REFUND_METHOD.REPLACEMENT, REFUND_METHOD.EXCHANGE].includes(returnItemsStore.returnSelections.refundOption?.choice),
);

const containsAreaRug = computed(() => returnItemsStore.CONTAINS_AREA_RUG_ITEM);

const getEstimatedRefund = (payload) => estimatedRefundStore.GET_ESTIMATED_REFUND(payload);

const setReturnEditable = (payload) => returnItemsStore.SET_RETURN_EDITABLE(payload);
const setReturnSelections = (payload) => returnItemsStore.SET_RETURN_SELECTIONS(payload);
const setIsReturnMethodIneligible = (payload) => returnItemsStore.SET_RETURN_METHOD_INELIGIBLE(payload);

const selectedReturnMethodDetails = computed(() =>
  returnMethodsConfig.value.find((method) => method.id === returnItemsStore.returnSelections.returnMethod),
);
const hasStoreDropOffReturnItem = computed(() => returnItemsStore.GET_SELECTED_RETURN_ITEMS?.filter((item) => item.blockedVendorFlag)?.length > 0);

const sortReturnMethod = () => {
  const returnMethod = [...returnMethodsConfig.value];

  if (containsAreaRug.value) {
    const storeReturn = returnMethod.find((rm) => rm.id === RETURN_METHOD_KEY_MAP.storeReturn);
    const filteredMethods = returnMethod.filter((rm) => rm.id !== RETURN_METHOD_KEY_MAP.storeReturn);
    return brand === 'bcom' ? filteredMethods : [...filteredMethods, storeReturn];
  }

  const sortOrder = returnItemsStore.returnItems?.returnDropOffMethods?.WEB ?? [];
  return returnMethod.sort((a, b) => sortOrder.indexOf(a.id) - sortOrder.indexOf(b.id));
};

const getReturnMethodsFromPrepareDeclarationResponse = (shippingMethods) => {
  /**
   * SSRE option selected, only show isRpEligible return methods based on that property.
   */
  const configMap = new Map(returnMethodsConfig.value.map((config) => [config.id, config]));
  if (isReplaceOrExchangeRefundSelected.value) {
    return shippingMethods
      .filter((method) => method.isRpEligible)
      .map((method) => configMap.get(method.dropOffMethod))
      .filter(Boolean);
  }
  return shippingMethods.map((method) => configMap.get(method.dropOffMethod)).filter(Boolean);
};
const validReturnMethods = () => {
  /**
   * RP return methods based on return method options returned from prepare declaration response.
   */
  const shippingMethods = returnItemsStore.prepareDeclarationResponse.returnMethods;
  if (shippingMethods && shippingMethods.length > 0) {
    returnMethods.value = getReturnMethodsFromPrepareDeclarationResponse(shippingMethods);
    returnMethodChoice.value = returnMethods.value[0]?.id || '';
    return;
    // TODO: ON SUBMIT update payload for v2 submitdeclaration
  }

  /*  For marketplace orders:
        - Returns UPS or FEDEX (if eligible) as the return method.
        - Also includes STORE (MSTORE/BSTORE) if `isBorisEnabled` KS is active.
    */
  if (returnItemsStore.IS_MARKETPLACE_ORDER) {
    returnMethodChoice.value = returnItemsStore.IS_FEDEX_ELIGIBLE ? RETURN_METHOD_KEY_MAP.fedex : RETURN_METHOD_KEY_MAP.ups;
    returnMethods.value = returnMethodsConfig.value.filter((method) => method.id === returnMethodChoice.value);
    if (returnItemsStore.returnItems.isBorisEnabled) {
      returnMethods.value.push(returnMethodsConfig.value.find((method) => method.id === RETURN_METHOD_KEY_MAP.storeReturn));
    }
    // FILTER STORE RETURN OPTION IF REBAG ITEM SELECTED FOR 3P item
    if (hasRebagItem.value) {
      returnMethods.value = returnMethods.value.filter((method) => method.id !== RETURN_METHOD_KEY_MAP.storeReturn);
    }
    return;
  }

  // Show only UPS, if the user selects Replacement or Exchange as the refund option
  if (isReplaceOrExchangeRefundSelected.value) {
    returnMethodChoice.value = RETURN_METHOD_KEY_MAP.ups;
    returnMethods.value = returnMethodsConfig.value.filter((method) => method.id === returnMethodChoice.value);
    return;
  }

  // Returns only STORE return method if it contains items with blockedVendorFlag
  if (hasStoreDropOffReturnItem.value) {
    returnMethodChoice.value = RETURN_METHOD_KEY_MAP.storeReturn;
    returnMethods.value = returnMethodsConfig.value.filter((method) => method.id === RETURN_METHOD_KEY_MAP.storeReturn);
    return;
  }

  const sortedReturnMethods = sortReturnMethod();

  // for mcom we return all return methods from config
  returnMethods.value = brand === 'mcom' ? sortedReturnMethods : sortedReturnMethods.filter((method) => method.id !== RETURN_METHOD_KEY_MAP.fedex);

  // With LABLE-FREE KS OFF, we show UPS & STORE return method
  const isLabelFreeReturnEnabled = returnItemsStore.returnItems?.isLabelFreeReturnEnabled ?? false;
  if (!isLabelFreeReturnEnabled) {
    returnMethods.value = returnMethods.value.filter((method) => method.id !== RETURN_METHOD_KEY_MAP.labelFree);
  }

  // If it contains hazmat, we do not show LABEL-FREE return method
  const isHazmatItemSelected = returnItemsStore.GET_SELECTED_RETURN_ITEMS?.some((item) => item.isHazmatItem);
  if (isHazmatItemSelected) {
    returnMethods.value = returnMethods.value.filter((method) => method.id !== RETURN_METHOD_KEY_MAP.labelFree);
  }
  // FILTER STORE RETURN OPTION IF REBAG ITEM SELECTED FOR 1P item
  if (hasRebagItem.value) {
    returnMethods.value = returnMethods.value.filter((method) => method.id !== RETURN_METHOD_KEY_MAP.storeReturn);
    returnMethodChoice.value = returnMethods.value[0].id;
  }
};

const defaultSelection = () => {
  if (returnItemsStore.IS_MARKETPLACE_ORDER) {
    returnMethodChoice.value = returnItemsStore.IS_FEDEX_ELIGIBLE ? RETURN_METHOD_KEY_MAP.fedex : RETURN_METHOD_KEY_MAP.ups;
  } else {
    returnMethodChoice.value = brand === 'mcom' && containsAreaRug.value ? '' : (returnMethods.value[0]?.id ?? RETURN_METHOD_KEY_MAP.storeReturn);
  }
};

watch(
  () => returnItemsStore.returnItemsEditable.returnMethod,
  (newState) => {
    if (!newState) {
      returnMethodChoice.value = returnItemsStore.returnSelections.returnMethod;
    } else {
      validReturnMethods();
      if (!returnItemsStore.returnSelections.returnMethod || returnItemsStore.isReturnMethodIneligible) {
        defaultSelection();
        fireTag('link', {
          event_name: 'return method',
          site_action: 'return method start',
          action_location: 'order details',
          return_fee_eligible:
            returnItemsStore.IS_ELIGIBLE_FOR_CONDITIONAL_RETURN_FEE && !returnItemsStore.hasEnrolledForStarRewards ? 'true' : 'false',
          loyalty_banner_visible:
            returnItemsStore.IS_ELIGIBLE_FOR_CONDITIONAL_RETURN_FEE && !returnItemsStore.hasEnrolledForStarRewards ? 'true' : 'false',
        });
      }
    }
  },
);

watch(
  () => joinStarRewardsStore.starReward.data,
  (starRewardData) => {
    if (starRewardData) {
      showStarRewardsMessage.value = true;
      setTimeout(() => {
        showStarRewardsMessage.value = false;
      }, 3000);
    }
  },
);

watch(hasEnrolledForStarRewards, (newValue) => {
  // If the user has enrolled for star rewards and the return method section is collapsed, expand the return method section.
  if (newValue && !returnItemsStore.returnItemsEditable.returnMethod) {
    setReturnEditable({
      returnItems: false,
      refundOptions: false,
      returnMethod: true,
    });
  }
  if (newValue) {
    fireTag('link', {
      event_name: 'return flow_loyalty drawer click',
      custom_link_name: 'return flow_loyalty drawer_join now',
      join_star_rewards: 'true',
    });
    fireTag('link', {
      event_name: 'return method',
      site_action: 'return method start',
      action_location: 'order details',
      return_fee_eligible: returnItemsStore.IS_ELIGIBLE_FOR_CONDITIONAL_RETURN_FEE && !returnItemsStore.hasEnrolledForStarRewards ? 'true' : 'false',
      loyalty_banner_visible:
        returnItemsStore.IS_ELIGIBLE_FOR_CONDITIONAL_RETURN_FEE && !returnItemsStore.hasEnrolledForStarRewards ? 'true' : 'false',
    });
  }
});

watch(containsAreaRug, (newValue) => {
  const isStoreReturn = brand === 'bcom' && returnItemsStore.returnSelections.returnMethod === RETURN_METHOD_KEY_MAP.storeReturn;
  showAreaRugErrorMessage.value = newValue && isStoreReturn;

  if (!newValue) showAreaRugErrorMessage.value = false;

  validReturnMethods();
});

const locatorLinkText = (linkText, returnMethod) => {
  if (containsAreaRug.value && returnMethod === RETURN_METHOD_KEY_MAP.storeReturn) {
    return `Eligible ${linkText.toLocaleLowerCase()}`;
  }
  return linkText;
};
const showLocatorSlideout = (event, returnDropOffMethod) => {
  if (event?.stopPropagation) {
    event.stopPropagation();
  }
  const wayFindingText = `wayfinding: ${getAnalyticsTitle(returnDropOffMethod)}`;
  fireTag('link', {
    event_name: wayFindingText,
    link_name: wayFindingText,
  });

  const {
    addressLine1 = '',
    addressLine2 = '',
    addressLine3 = '',
    city = '',
    state = '',
    postalCode = '',
  } = {
    ...(returnItemsStore.returnItems?.shippingAddressInfo?.address ??
      (UserStatus.isSignedIn() ? returnItemsStore.returnItems?.billingAddressInfo : {})),
  };

  const address = [addressLine1, addressLine2, addressLine3, city, state, postalCode].filter((i) => i).join(', ');
  registerSlideout({
    id: 'return-method-slideout',
    title: getTitle(returnDropOffMethod, containsAreaRug.value),
    elementIdToFocus: event?.target?.id,
    props: {
      typeOf: returnDropOffMethod,
      address,
      useMspLocatorApi: true,
      locationRadius: LOCATOR_LOCATION_RADIUS,
      resultsDisplayed: LOCATOR_RESULT_LIMIT,
      showMyStoreCTA: false,
      newGoogleApiCall: returnItemsStore.returnItems?.isNewGoogleAPICallEnabled,
      areaRugItems: containsAreaRug.value,
      separateOutlets: true,
      page: 'returns',
      excludeGob: true,
      storeInPreferenceDatabaseEnabled: returnItemsStore.returnItems?.storeInPreferenceDatabaseEnabled,
      newPlacesDetailsApiEnabled: returnItemsStore.returnItems?.newPlacesDetailsApiEnabled,
      commonLocationServiceApiKey: envPropsStore.envProps.xMacysApiKey,
    },
    component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "locator-slideout" */ '@nuxt-common/locator/app/components/Locator'))),
  });
  toggleSlideout('return-method-slideout');
};
const onCardClick = (option, event) => {
  if (returnItemsStore.isReturnMethodIneligible) {
    setIsReturnMethodIneligible(false);
  }
  // If the selected items include an area rug and the user clicks on Macy's store card for the first time, the drawer should automatically open.
  if (containsAreaRug.value && !hasInteracted.value && option === RETURN_METHOD_KEY_MAP.storeReturn) {
    hasInteracted.value = true;
    showLocatorSlideout(event, option);
  }
};
const getShipmentFee = (returnMethod) => {
  return brand === 'mcom' && CONDITIONAL_RETURN_FEE_ELIGIBLE_RETURN_METHODS.includes(returnMethod)
    ? `$${returnItemsStore.returnItems?.conditionalReturnFee}`
    : FREE_SHIPMENT;
};
const onChange = () => {
  setReturnEditable({
    returnItems: false,
    refundOptions: false,
    returnMethod: true,
  });
  fireTag('link', {
    link_name: 'change return method',
    event_name: 'change return method',
  });
};
const callEstimatedRefund = async () => {
  try {
    isEstimatedRefundLoading.value = true;
    const payload = createPayloadFromUopResponse(
      returnItemsStore.returnItems,
      returnItemsStore.returnSelections,
      returnMethodChoice.value,
      returnItemsStore.APPLY_CONDITIONAL_RETURN_FEE,
      returnItemsStore.IS_REGISTRY_ORDER,
    );
    await getEstimatedRefund({ payload });
  } finally {
    isEstimatedRefundLoading.value = false;
  }
};

const returnShippingFeeModal = () => {
  registerModal({
    id: 'return-shipping-fee',
    title: 'Are you sure you want to pay for return shipping?',
    width: '1062px',
    props: {
      shippingFee: `${returnItemsStore.returnItems?.conditionalReturnFee}`,
      returnMethod: returnMethodChoice.value,
    },
    component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "join-now" */ '@/components/returns/ReturnShippingFee.vue'))),
  });

  toggleModal('return-shipping-fee');
  showShippingFeeModal.value = false;
};
const onContinue = async () => {
  setReturnSelections({ returnMethod: returnMethodChoice.value });
  await callEstimatedRefund();

  if (
    CONDITIONAL_RETURN_FEE_ELIGIBLE_RETURN_METHODS.includes(returnMethodChoice.value) &&
    returnItemsStore.IS_ELIGIBLE_FOR_CONDITIONAL_RETURN_FEE &&
    showShippingFeeModal.value &&
    !returnItemsStore.hasEnrolledForStarRewards
  ) {
    returnShippingFeeModal();
  }

  setReturnEditable({
    returnItems: false,
    refundOptions: false,
    returnMethod: false,
  });
  fireTag('link', {
    event_name: 'return method',
    site_action: 'return method complete',
    action_location: 'order details',
    return_method_selected: returnMethodChoice.value,
  });
  showAreaRugErrorMessage.value = false;
};

const getRedirectPath = () => {
  const routePath = route.fullPath;
  return `${window.location.origin}${routePath}`;
};

const joinNowHandler = async (event) => {
  loadingJoinRewards.value = true;
  fireTag('link', {
    event_name: 'return flow_loyalty banner click',
    custom_link_name: 'return flow_loyalty banner_join now',
  });

  if (UserStatus.isSignedIn()) {
    joinStarRewardsStore.RESET_STAR_REWARDS();
    registerSlideout({
      id: JOIN_STAR_REWARD_SLIDEOUT_TOGGLE_ID,
      title: 'Join Star Rewards For Free',
      elementIdToFocus: event?.target?.id,
      props: {
        uopReturnItems: true,
      },
      component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "join-now" */ '@/components/JoinStarRewards.vue'))),
    });
    loadingJoinRewards.value = false;
    toggleSlideout(JOIN_STAR_REWARD_SLIDEOUT_TOGGLE_ID);
  } else {
    loadingJoinRewards.value = false;
    const domain = envPropsStore.envProps.cookieDomain;
    Cookie.set('FORWARDPAGE_KEY', getRedirectPath(), null, { domain });
    window.location.href = `/account/signin?fromPage=purchases&starRewardsPromotion=${returnItemsStore.IS_ELIGIBLE_FOR_CONDITIONAL_RETURN_FEE}`;
  }
};

onMounted(() => {
  validReturnMethods();
});
</script>

<style lang="scss" scoped>
#return-method {
  @include breakpoint(large up) {
    min-height: 630px;
  }
  ul li {
    list-style: none;
  }
  .join-star-rewards {
    background-color: $loyalty-bg-1-color;
  }
}
</style>
